import { supabase } from "@/libs/supabase";
import type { Diary, DbDiary, DiaryInput } from "@/types/diary";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

// データベースの型からアプリケーションの型への変換関数
export const convertToAppDiary = (dbDiary: DbDiary): Diary => ({
	id: dbDiary.id,
	content: dbDiary.content,
	userId: dbDiary.user_id,
	createdAt: dbDiary.created_at,
	updatedAt: dbDiary.updated_at,
	title: dbDiary.title,
});

export const diaryService = {
	// 日記一覧の取得
	async getDiaries() {
		const { data, error } = await supabase
			.from("diaries")
			.select("*")
			.order("created_at", { ascending: false });

		if (error) throw error;
		return (data as unknown as DbDiary[]).map(convertToAppDiary);
	},

	// 特定の日記の取得
	async getDiaryById(id: string) {
		const { data, error } = await supabase
			.from("diaries")
			.select("*")
			.eq("id", id)
			.single();

		if (error) throw error;
		return convertToAppDiary(data as unknown as DbDiary);
	},

	// 日記の作成
	async createDiary(diary: DiaryInput) {
		const { data, error } = await supabase
			.from("diaries")
			.insert([
				{
					title: diary.title,
					content: diary.content,
					user_id: (await supabase.auth.getUser()).data.user?.id,
				},
			])
			.select()
			.single();

		if (error) throw error;
		return convertToAppDiary(data as unknown as DbDiary);
	},

	// 日記の更新
	async updateDiary(id: string, diary: Partial<DiaryInput>) {
		const { data, error } = await supabase
			.from("diaries")
			.update({
				title: diary.title,
				content: diary.content,
			})
			.eq("id", id)
			.select()
			.single();

		if (error) throw error;
		return convertToAppDiary(data as unknown as DbDiary);
	},

	// 日記の削除
	async deleteDiary(id: string) {
		const { error } = await supabase.from("diaries").delete().eq("id", id);

		if (error) throw error;
	},

	// リアルタイム更新のサブスクリプション
	subscribeToChanges(
		callback: (payload: RealtimePostgresChangesPayload<DbDiary>) => void,
	) {
		return supabase
			.channel("diary_changes")
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "diaries",
				},
				callback,
			)
			.subscribe();
	},
};
